import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, CheckCircle2, XCircle, Send, Phone } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTestConnection } from '@/hooks/useChannelConnections';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/error-handler';

const testConnectionSchema = z.object({
  testPhoneNumber: z
    .string()
    .min(10, 'Número deve ter pelo menos 10 dígitos')
    .max(15, 'Número deve ter no máximo 15 dígitos')
    .regex(/^\d+$/, 'Número deve conter apenas dígitos')
    .optional()
    .or(z.literal('')),
});

type TestConnectionFormData = z.infer<typeof testConnectionSchema>;

interface TestConnectionSheetProps {
  channelId: string;
  channelName: string;
  provider: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * TestConnectionSheet - Mobile-first Sheet component
 *
 * Allows user to:
 * 1. Validate credentials only (without phone number)
 * 2. Send a real test message (with phone number)
 */
export function TestConnectionSheet({
  channelId,
  channelName,
  provider,
  isOpen,
  onClose,
}: TestConnectionSheetProps) {
  const { mutate: testConnection, isPending } = useTestConnection(channelId, provider);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TestConnectionFormData>({
    resolver: zodResolver(testConnectionSchema),
    defaultValues: {
      testPhoneNumber: '',
    },
  });

  const handleClose = () => {
    setTestResult(null);
    reset();
    onClose();
  };

  const onSubmit = (data: TestConnectionFormData) => {
    setTestResult(null);

    const requestData = data.testPhoneNumber
      ? { testPhoneNumber: data.testPhoneNumber }
      : undefined;

    testConnection(requestData, {
      onSuccess: (result) => {
        setTestResult({
          success: result.success,
          message: result.message,
        });

        if (result.success) {
          toast.success(
            data.testPhoneNumber
              ? 'Mensagem de teste enviada!'
              : 'Credenciais validadas!',
            {
              description: result.message,
            }
          );
        } else {
          toast.error('Falha no teste', {
            description: result.message,
          });
        }
      },
      onError: (error) => {
        const message = getErrorMessage(error, 'Erro ao testar conexão');
        setTestResult({
          success: false,
          message,
        });
        toast.error('Erro ao testar conexão', {
          description: message,
        });
      },
    });
  };

  const handleValidateOnly = () => {
    onSubmit({ testPhoneNumber: '' });
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Testar Conexão</SheetTitle>
          <SheetDescription>
            Teste a conexão do canal <strong>{channelName}</strong>
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
          {/* Result Alert */}
          {testResult && (
            <Alert variant={testResult.success ? 'default' : 'destructive'}>
              {testResult.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <AlertDescription>{testResult.message}</AlertDescription>
            </Alert>
          )}

          {/* Phone Number Input */}
          <div className="space-y-2">
            <Label htmlFor="testPhoneNumber" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Número para Teste (opcional)
            </Label>
            <Input
              id="testPhoneNumber"
              type="tel"
              placeholder="5511999998888"
              {...register('testPhoneNumber')}
              disabled={isPending}
            />
            <p className="text-xs text-muted-foreground">
              Informe o número com código do país (ex: 5511999998888).
              Se não informar, apenas validará as credenciais.
            </p>
            {errors.testPhoneNumber && (
              <p className="text-xs text-destructive">
                {errors.testPhoneNumber.message}
              </p>
            )}
          </div>

          <SheetFooter className="flex-col gap-2 sm:flex-col">
            {/* Send Test Message Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Enviar Mensagem de Teste
                </>
              )}
            </Button>

            {/* Validate Only Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleValidateOnly}
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validando...
                </>
              ) : (
                'Apenas Validar Credenciais'
              )}
            </Button>

            {/* Cancel Button */}
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleClose}
              disabled={isPending}
            >
              Fechar
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
